import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View, useWindowDimensions } from 'react-native';

import { Route, useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import ConfettiLottieJSON from '@/assets/animations/confetti.json';
import BackArrow from '@/assets/icons/backArrow';
import { CheckmarkLargeWhite } from '@/assets/icons/checkmark-large-white';
import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CxtType, addCxtTag, removeCxtTag } from '@/feature/analytics/connextraTag';
import { AnalyticsEvent, RUDDERSTACK_EVENTS } from '@/feature/analytics/constants';
import { trackRudderStackEvent } from '@/feature/analytics/trackRudderStackEvent';
import { Banner } from '@/feature/betslip-sbk/components/Banner';
import { popToRipsGameWebView } from '@/feature/lobby-rips/utils/navigation';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { hasMadeFirstDepositSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { SuccessfulDepositNavigationProp } from '@/navigation/deposit/types';
import {
    findProductBetSlip,
    isRouteInHistory,
    popToHome,
    popToProductBetSlip,
    resetToAccountScreen,
} from '@/navigation/navigation';
import { DepositStackParamList } from '@/navigation/types';
import { isWeb } from '@/utils/constants-platform-specific';
import LottieView from 'lottie-react-native';

import { PaymentMethodTypes } from '../const';
import { PaymentMethodType } from '../hooks/use-deposit-payment-methods';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useTrackFirstDepositEvent } from '../hooks/use-track-first-deposit-event';
import { PayPalStatus } from '../utils';

const styles = StyleSheet.create({
    rightArrow: { transform: [{ rotate: '-180deg' }] },
    animContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: '50%',
        left: '50%',
    },
    lottie: {
        width: '100%',
        height: '100%',
    },
});

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'SuccessfulDepositModal'>;

export const SuccessfulDeposit = ({ route }: ScreenProps) => {
    const navigation = useNavigation<SuccessfulDepositNavigationProp>();

    const rootNavigation = useRootNavigation();

    const { t } = useTranslation(['wallet', 'common', 'kyc']);

    const tSocialWallet = useSocialWalletTranslation('transactions');
    const { activeWalletProductName, activeWalletProduct, isSharedSocialWallet } = useActiveWallet();
    const { product } = useJurisdictionStore();
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const clearDepositStatus = useDepositStatusStore(state => state.actions.clearDepositStatus);

    const { data: hasMadeFirstDeposit, isLoading } = useAuthUserConfig({
        select: hasMadeFirstDepositSelector,
    });
    const [isFtdAttempt, setIsFtdAttempt] = useState(false);
    const { width, height } = useWindowDimensions();
    const maxWidth = isWeb ? Math.min(800, width) : width;
    const maxHeight = isWeb ? Math.min(800, height) : height;
    const {
        paymentProvider,
        title,
        description,
        disableTransition: shouldDisableProductTransition = true,
        isRestrictedBin = false,
    } = route.params;
    const isFantasyMoneyPurchase = paymentProvider === PaymentMethodTypes.FantasyMoney;
    const isSocialWalletFunding = paymentProvider === PaymentMethodTypes.SocialWallet;

    useEffect(() => {
        // hasMadeFirstDeposit is initially set to false when the screen is first loaded but soon changes to true.
        // This causes the animation to be skipped and disappear immediately. isFirstAttempt is used to prevent this issue.
        if (!isLoading && !hasMadeFirstDeposit) {
            setIsFtdAttempt(true);
        }
    }, [hasMadeFirstDeposit, isLoading]);

    usePreventAndroidBackNavigation();
    useTrackFirstDepositEvent(PayPalStatus.SUCCESS, paymentProvider, selectedAmount);

    useEffect(() => {
        if (product === Product.Pickem) {
            addCxtTag({
                pageType: isFtdAttempt ? CxtType.FIRST_DEPOSIT_CONFIRM : CxtType.DEPOSIT_CONFIRM,
                includeAccountId: true,
                value: selectedAmount,
                currency: 'USD',
            });
        }

        return () => {
            removeCxtTag();
        };
    }, [isFtdAttempt, product, selectedAmount]);

    const trackDepositSuccess = useCallback(() => {
        const eventPayload = { method: paymentProvider };
        trackRudderStackEvent(RUDDERSTACK_EVENTS.custom(AnalyticsEvent.DEPOSIT_SUCCESS), eventPayload);
        BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_SUCCESS, eventPayload);
    }, [paymentProvider]);

    const navigationState = navigation.getState();
    const depositRoute = navigationState?.routes.find(localRoute => localRoute.name === 'DepositScreen') as Route<
        'DepositScreen',
        DepositStackParamList['DepositScreen']
    >;

    const shouldRedirectDepositToLobby = depositRoute?.params?.shouldRedirectToLobby ?? true;

    const renderDescription = () => {
        if (description) {
            return description;
        }
        if (isSharedSocialWallet) {
            if (isFantasyMoneyPurchase) {
                return t('fantasy_money_successful_coin_purchase');
            }
            return t('coin_purchase_requested_description', {
                loginMessage: getPaymentProviderLoginMessage(paymentProvider),
            });
        }
        return t('deposit_success_modal_description', {
            loginMessage: getPaymentProviderLoginMessage(paymentProvider),
        });
    };

    const handleClose = useCallback(() => {
        trackDepositSuccess();
        clearDepositStatus();

        const betSlipStack = findProductBetSlip(rootNavigation);

        if (betSlipStack) {
            popToProductBetSlip(rootNavigation, betSlipStack);
            return;
        }

        if (isRouteInHistory(rootNavigation, 'RipsGameWebView')) {
            popToRipsGameWebView(navigation);
            return;
        }

        // DepositScreen was opened with params asking for a lobby redirect, so go through the transition
        if (shouldRedirectDepositToLobby) {
            if (depositRoute?.params?.disableTransition || shouldDisableProductTransition) {
                // switch to the active wallet's product lobby if it differs from the current product
                if (product !== activeWalletProduct) {
                    useProductTransitionStore.getState().actions.triggerTransition(activeWalletProduct);
                    return;
                }
                popToHome(rootNavigation, 'lobby');
                return;
            }
            useProductTransitionStore.getState().actions.triggerTransition(product);
            return;
        }

        // if the race to place sheet is open, return to the lobby by popping to it
        // so the sheet is already open when we return
        if (depositRoute?.params?.shouldPopToLobby) {
            popToHome(rootNavigation, 'lobby');
            return;
        }

        popToHome(rootNavigation, 'account');
    }, [
        activeWalletProduct,
        clearDepositStatus,
        depositRoute?.params?.disableTransition,
        depositRoute?.params?.shouldPopToLobby,
        navigation,
        rootNavigation,
        product,
        shouldDisableProductTransition,
        shouldRedirectDepositToLobby,
        trackDepositSuccess,
    ]);

    const handleSetGamingLimits = useCallback(() => {
        trackDepositSuccess();
        clearDepositStatus();
        resetToAccountScreen(rootNavigation, 'ResponsibleGaming');
    }, [clearDepositStatus, rootNavigation, trackDepositSuccess]);

    return (
        <Screen edges={['top', 'bottom']}>
            {isFtdAttempt ? (
                <View
                    style={[
                        styles.animContainer,
                        {
                            maxWidth: maxWidth,
                            maxHeight: maxHeight,
                            transform: [{ translateX: -maxWidth / 2 }, { translateY: -maxHeight / 2 }],
                        },
                    ]}
                    pointerEvents="none"
                >
                    <LottieView source={ConfettiLottieJSON} style={styles.lottie} autoPlay loop={false} />
                </View>
            ) : null}
            {shouldRedirectDepositToLobby ? null : <ScreenNavBar closeIconMode="close" onClose={handleClose} />}
            <MaxWidthWrapper flex={1} paddingBottom={'s16'}>
                <Box flex={1} justifyContent="space-between" paddingHorizontal="s16">
                    <Box flex={1} justifyContent="center" alignItems="center">
                        <CheckmarkLargeWhite />
                        <Text textAlign="center" variant="headlineMedium" mt="s16">
                            {title ||
                                (isSocialWalletFunding
                                    ? t('social_wallet_funding_success_title')
                                    : isSharedSocialWallet
                                    ? t('coin_purchase_requested')
                                    : t('deposit_success_modal_title', { product: activeWalletProductName }))}
                        </Text>
                        <Text textAlign="center" color={ds.colorToken.text.secondary} variant="bodyMedium" mt="s8">
                            {renderDescription()}
                        </Text>
                        {isRestrictedBin ? (
                            <Text textAlign="center" color={ds.colorToken.text.secondary} variant="bodyMedium" mt="s16">
                                {tSocialWallet('restricted_bin_successful_deposit_message')}
                            </Text>
                        ) : null}
                    </Box>
                    <Box gap="s16">
                        <Banner
                            message={isSharedSocialWallet ? t('kyc:set_purchase_limits') : t('kyc:set_gaming_limits')}
                            onPress={handleSetGamingLimits}
                            rightIcon={
                                <BackArrow
                                    height={16}
                                    width={16}
                                    viewBox="0 0 25 25"
                                    marginLeft={12}
                                    style={styles.rightArrow}
                                />
                            }
                        />
                        <Button
                            label={
                                shouldRedirectDepositToLobby
                                    ? t('common:go_to_product_lobby', { product: activeWalletProductName })
                                    : t('common:done')
                            }
                            hierarchy={'primary'}
                            onPress={handleClose}
                            testID="depositDoneButton"
                        />
                    </Box>
                </Box>
            </MaxWidthWrapper>
        </Screen>
    );
};

const getPaymentProviderLoginMessage = (paymentProvider: PaymentMethodType): string => {
    const loginMessages: Record<PaymentMethodType, string> = {
        [PaymentMethodTypes.Paysafe]: 'linked your Debit card.',
        [PaymentMethodTypes.Paysafe3ds]: 'linked your Debit card.',
        [PaymentMethodTypes.PaysafePaypal]: 'logged into your PayPal account.',
        [PaymentMethodTypes.PaysafeMazooma]: 'logged into your Bank account.',
        [PaymentMethodTypes.PaysafePayByBank]: 'logged into your Bank account.',
        [PaymentMethodTypes.SocialWallet]: 'moved funds from your Social Wallet.',
    };

    return loginMessages[paymentProvider];
};
